"use client";
import { CheckCircleOutlined, CloseCircleOutlined, FilterOutlined } from "@ant-design/icons";
import { Button, Checkbox, Popover, Space, Typography } from "antd/lib";
import dayjs from "dayjs";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import useAxiosQuery from "@/hooks/useMyQuery";
import MyCard from "@/app/components/MyCard";
import Heading from "@/app/components/Heading";
import { useTranslations } from "next-intl";
import type { MyColumnType } from "@/app/components/MyTable";
import CustomTable from "@/app/components/MyTable";
import type { ISignal } from "@/models/signal";
import { DateTimeFormat } from "@/constant";
import { TokenInfo } from "@/app/components/Signals";
import LinkToKlardaApp from "@/app/components/LinkToKlardaApp";
import { useTimezoneContext } from "@/context/TimezoneContext";

const limit = 10;

const signalTypes = [
  { label: "52-week high (Intraday)", value: "52-week high (Intraday)", defaultFixed: true },
  { label: "52-week high (EOD)", value: "52-week high (EOD)", defaultFixed: true },
  { label: "52-week low (Intraday)", value: "52-week low (Intraday)", defaultFixed: true },
  { label: "52-week low (EOD)", value: "52-week low (EOD)", defaultFixed: true },
  { label: "Death Cross", value: "Death Cross", defaultFixed: true },
  { label: "Golden Cross", value: "Golden Cross", defaultFixed: true },
  { label: "SMA200 EOD Cross Above", value: "SMA200 EOD Cross Above", defaultFixed: true },
  { label: "SMA200 EOD Cross Below", value: "SMA200 EOD Cross Below", defaultFixed: true },
  { label: "Bollinger-band bearish cross", value: "Bollinger-band bearish cross", defaultFixed: true },
  { label: "Bollinger-band bullish cross", value: "Bollinger-band bullish cross", defaultFixed: true },
  { label: "CCI bullish cross (EOD)", value: "CCI bullish cross (EOD)", defaultFixed: true },
  { label: "CCI bearish cross (EOD)", value: "CCI bearish cross (EOD)", defaultFixed: true },
];

function ProductSignal({ symbol }: { symbol: string }) {
  const [categories, switchCategories] = useState<string>("");
  const filter = useMemo(() => categories.split(",").filter(s => !!s), [categories]);
  const t = useTranslations("products");
  const { timezone } = useTimezoneContext();

  useEffect(() => {
    handleResetFilter();
  }, []);

  const { data: response, isLoading } = useAxiosQuery<{ data: { total?: number; result?: ISignal[] } }>({
    url: "/v1/crypto-signal/signal_list",
    enabled: !!symbol,
    params: {
      symbol,
      page: 1,
      limit,
      is_watchlist: false,
      signal_type: filter?.length > 0 ? filter.join(",") : "null",
    },
    includeParamsInQueryKey: true,
  });

  const isSelectAll = useMemo(() => signalTypes.every(c => filter.includes(c.value)), [filter, t]);

  const handleResetFilter = useCallback(() => {
    switchCategories(signalTypes.map(i => i.value).join(",") ?? "");
  }, [switchCategories]);

  const columns = useMemo<MyColumnType<ISignal>[]>(
    () => [
      {
        title: t("Time"),
        key: "signal_time",
        dataIndex: "signal_time",
        className: "h-[42.6px]",
        sorter: true,
        width: 200,
        render: (_, record) => {
          const day = dayjs(record.signal_time).tz(timezone.utc);
          return (
            <Typography.Text title={record.signal_time}>
              {day.isBefore(dayjs().tz(timezone.utc).subtract(24, "h")) ? day.format(DateTimeFormat) : day.fromNow()}
            </Typography.Text>
          );
        },
      },
      {
        title: t("Asset"),
        key: "asset",
        dataIndex: "asset",
        className: "h-[42.6px]",
        width: 200,
        sorter: (r1, r2) => (r1.symbol || r1.name || "").localeCompare(r2.symbol || r2.name || ""),
        render: (_, record) => <TokenInfo data={record} />,
      },
      {
        title: t("Buy_Sell"),
        key: "buy_sell",
        className: "h-[42.6px]",
        dataIndex: "buy_sell",
        sorter: true,
        width: 100,
        align: "left",
        render: (_, record) => (
          <Typography.Text type={record.buy_sell ? "success" : "danger"}>
            {record.buy_sell ? t("Buy") : t("Sell")}
          </Typography.Text>
        ),
      },
      {
        title: t("Back_test"),
        key: "confirm",
        className: "h-[42.6px]",
        dataIndex: "confirm",
        width: 130,
        align: "left",
        render: (_, record) => (
          <Typography.Text type={record.confirm === 1 ? "success" : "danger"}>
            {record.confirm ? <CheckCircleOutlined /> : <CloseCircleOutlined />}{" "}
            {record.confirm ? t("Correct") : t("Missed")}
          </Typography.Text>
        ),
      },
      {
        title: t("Signal"),
        key: "signal",
        className: "h-[42.6px]",
        dataIndex: "signal",
        align: "left",
        width: 200,
        sorter: true,
        render: text => <Typography.Text type={text}>{text}</Typography.Text>,
      },
    ],
    [t, timezone.utc],
  );

  return (
    <MyCard
      className="h-full"
      title={<Heading title={t("Signals")} />}
      extra={
        <Space>
          <Popover
            placement={"bottomRight"}
            showArrow={false}
            rootClassName="min-w-[265px]"
            content={
              <Space direction={"vertical"} size={12} className={"w-full"}>
                <div className={"w-full flex gap-2 justify-between items-center"}>
                  <Typography.Title level={5} className={"m-0"}>
                    {t("Signals_types")}
                  </Typography.Title>
                </div>
                <Space className={"w-full justify-between"}>
                  <Checkbox
                    checked={isSelectAll}
                    onClick={() => {
                      if (!isSelectAll) {
                        const newFilter = signalTypes
                          .filter(l => !filter?.includes(l.value))
                          .map(f => f?.value)
                          .concat(filter);
                        switchCategories(newFilter.length ? newFilter.join(",") : "");
                      } else {
                        switchCategories("");
                      }
                    }}
                  >
                    {t("Select_all")}
                  </Checkbox>
                  <Button onClick={handleResetFilter} type={"link"}>
                    {t("Reset")}
                  </Button>
                </Space>
                <Space direction={"vertical"} className="pl-2">
                  {signalTypes
                    .sort((c1, c2) => c1.value.localeCompare(c2.value))
                    .map((c, index) => (
                      <Checkbox
                        key={index}
                        checked={filter.includes(c.value)}
                        onChange={() => {
                          const newFilter = filter.includes(c.value)
                            ? filter.filter(l => l !== c.value)
                            : filter.concat([c.value]);
                          switchCategories(newFilter.length ? newFilter.join(",") : "");
                        }}
                      >
                        <Typography.Text>{c.label}</Typography.Text>
                      </Checkbox>
                    ))}
                </Space>
              </Space>
            }
          >
            <Button type={"default"} icon={<FilterOutlined />}>
              {t("Filters")}
            </Button>
          </Popover>
        </Space>
      }
    >
      <div className="flex flex-col gap-2">
        <CustomTable
          isLoading={isLoading}
          columns={columns}
          dataSource={response?.data?.result ?? []}
          scroll={{ x: 720, y: 400 }}
        />
        <LinkToKlardaApp isOnlyText />
      </div>
    </MyCard>
  );
}

export default memo(ProductSignal);
